import React, { useContext, useEffect, useReducer, useRef, useState } from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useSocketListener from "../../hooks/useSocketListener";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TaskStatusChip, TaskPriorityChip, TaskTypeChip } from "../TaskChips";
import TaskDetailsModal from "../TaskDetailsModal";
import { formatTaskDate, mergeTasksById, normalizeId } from "../../pages/Tasks/taskUtils";

const useStyles = makeStyles(theme => ({
  centered: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2)
  },
  item: {
    padding: theme.spacing(1, 0),
    cursor: "pointer",
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    }
  },
  title: {
    fontWeight: 600,
    overflowWrap: "break-word",
    wordBreak: "break-word"
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    margin: theme.spacing(0.5, 0)
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  }
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":
      return mergeTasksById(state, action.payload);
    case "REMOVE":
      return state.filter(task => normalizeId(task.id) !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

// Ticket vinculado: só texto curto (UUID), nunca link, nunca navegação,
// nunca o id numérico interno, e nunca uma linha vazia/inválida quando não
// há ticket ou o ticket não tem uuid.
const TicketReference = ({ task }) => {
  if (!task.ticket || !task.ticket.uuid) return null;

  return (
    <Typography variant="caption" color="textSecondary">
      {i18n.t("tasks.contactTab.ticket")}: #{task.ticket.uuid.slice(0, 8)}
    </Typography>
  );
};

// Aba "Tarefas" do ContactDrawer — lista somente-consulta das tarefas
// vinculadas a um contato (GET /tasks?contactId=). Só existe montada
// enquanto a aba estiver ativa (o ContactDrawer só a renderiza dentro de
// um TabPanel condicional) — nunca busca nada antes disso. Clique num
// item abre o TaskDetailsModal já existente, em modo readOnly.
const ContactTasksTab = ({ contactId }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const normalizedContactId = normalizeId(contactId);

  const [tasks, dispatch] = useReducer(reducer, []);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const realtimeDebounceRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchPage = (targetPage, isLoadMore) => {
    // Nunca consulta com um contactId inválido — mesmo que isso não deva
    // acontecer (o ContactDrawer já valida antes de montar esta aba).
    if (normalizedContactId === null) return;

    const currentRequestId = ++requestIdRef.current;
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    (async () => {
      try {
        const { data } = await api.get("/tasks", {
          params: { contactId: normalizedContactId, pageNumber: targetPage }
        });

        if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;

        dispatch({ type: "LOAD", payload: Array.isArray(data.tasks) ? data.tasks : [] });
        setHasMore(Boolean(data.hasMore));
        setCount(Number(data.count) || 0);
      } catch (err) {
        if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;
        toastError(err);
      } finally {
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    })();
  };

  useEffect(() => {
    // Troca de contato (ou montagem inicial): invalida qualquer requisição
    // anterior, zera lista/página/hasMore e busca a página 1 do zero.
    requestIdRef.current += 1;
    dispatch({ type: "RESET" });
    setHasMore(false);
    setPageNumber(1);
    setCount(0);
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedContactId]);

  // Limpa o timer de debounce ao desmontar E sempre que o contato mudar —
  // nunca deixa um timer antigo, agendado para o contato anterior, disparar
  // depois da troca.
  useEffect(() => {
    return () => {
      if (realtimeDebounceRef.current) {
        clearTimeout(realtimeDebounceRef.current);
        realtimeDebounceRef.current = null;
      }
    };
  }, [normalizedContactId]);

  const handleRealtimeTaskEvent = () => {
    if (normalizedContactId === null) return;

    // Vários eventos em sequência geram no máximo uma atualização da
    // página 1 — nunca uma chamada por evento.
    if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
    realtimeDebounceRef.current = setTimeout(() => {
      dispatch({ type: "RESET" });
      setHasMore(false);
      setPageNumber(1);
      fetchPage(1, false);
    }, 500);
  };

  // O evento é só um sinal opaco ({action:"refresh"}), sem taskId nem
  // dados da tarefa — só dispara uma nova consulta pelo endpoint
  // autenticado, nunca confia em nada vindo do socket.
  useSocketListener(socket, user, "task", handleRealtimeTaskEvent, [normalizedContactId]);

  const loadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchPage(nextPage, true);
  };

  const handleTaskUpdated = updatedTask => {
    dispatch({ type: "LOAD", payload: [updatedTask] });
  };

  // 404: a tarefa sumiu/ficou inacessível — o próprio TaskDetailsModal já
  // fecha sozinho; aqui só removemos da lista local, sem nova consulta.
  const handleTaskUnavailable = removedTaskId => {
    const normalizedRemovedTaskId = normalizeId(removedTaskId);
    dispatch({ type: "REMOVE", payload: normalizedRemovedTaskId });
  };

  const handleKeyDownItem = (e, taskId) => {
    if (e.key === "Enter") {
      setSelectedTaskId(taskId);
    } else if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      setSelectedTaskId(taskId);
    }
  };

  return (
    <Box>
      {selectedTaskId && (
        <TaskDetailsModal
          taskId={selectedTaskId}
          open={Boolean(selectedTaskId)}
          onClose={() => setSelectedTaskId(null)}
          onTaskUpdated={handleTaskUpdated}
          onTaskUnavailable={handleTaskUnavailable}
          readOnly
        />
      )}

      <Typography variant="subtitle2" gutterBottom>
        {i18n.t("tasks.contactTab.title")} ({count})
      </Typography>

      {loading ? (
        <Box className={classes.centered}>
          <CircularProgress size={24} />
        </Box>
      ) : tasks.length === 0 ? (
        <Box className={classes.centered}>
          <Typography color="textSecondary">{i18n.t("tasks.contactTab.empty")}</Typography>
        </Box>
      ) : (
        <>
          {tasks.map((task, index) => (
            <React.Fragment key={task.id}>
              <Box
                className={classes.item}
                onClick={() => setSelectedTaskId(task.id)}
                onKeyDown={e => handleKeyDownItem(e, task.id)}
                role="button"
                tabIndex={0}
                aria-label={`${i18n.t("tasks.details.title")}: ${task.title}`}
              >
                <Typography variant="body2" className={classes.title}>
                  {task.title}
                </Typography>

                <Box className={classes.chipsRow}>
                  <TaskStatusChip status={task.status} />
                  <TaskPriorityChip priority={task.priority} />
                  <TaskTypeChip type={task.type} />
                </Box>

                <Box className={classes.metaRow}>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("tasks.table.assignedUser")}:{" "}
                    {task.assignedUser?.name || i18n.t("tasks.table.none")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("tasks.table.dueDate")}:{" "}
                    {formatTaskDate(task.dueDate) || i18n.t("tasks.table.noDueDate")}
                  </Typography>
                  <TicketReference task={task} />
                </Box>
              </Box>
              {index < tasks.length - 1 && <Divider />}
            </React.Fragment>
          ))}

          {hasMore && (
            <Box className={classes.centered}>
              <Button onClick={loadMore} disabled={loadingMore} size="small">
                {loadingMore ? <CircularProgress size={18} /> : i18n.t("tasks.contactTab.loadMore")}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ContactTasksTab;
